<template>
  <div class="auth-token-manager">
    <template v-if="isAuthenticatedAsAdmin">
      <div style="display: flex; align-items: end; gap: 1rem">
        <select-input :include-blank="false" v-model="tempTokenTtl" style="margin-bottom: 0; margin-top: 0 !important;" :collection="ttlOptions" :label="'Dauer'"/>
      <text-input v-model="tokencomment" style="margin-bottom: 0" :label="$t('fields.comment') + '*'"></text-input>
      <common-button
        @click="issueTempToken"
        :disabled="!hasTokencomment"
        icon="plus"
      >{{ $t('buttons.issueToken') }}</common-button>
      <revoke-all-tokens-button style="margin-left: auto" :subject="resource" @done="clearAuthTokens" />
      </div>
    </template>

    <data-grid
      :columns="gridColumns"
      :rows="gridRows"
      class="uk-margin-medium"
    >
      <span slot="empty">{{$t('hints.empty')}}</span>
      <template scope="props" slot="row">
        <td>
          <time-to-go :datetime="props.row.expiresAt" />
        </td>
        <td>
          <span v-if="props.row.comment">
            {{props.row.comment}}
          </span>
          <span style="color: red" v-else>
            Keiner angegeben
          </span>
        </td>
        <td>
          <timeago :auto-update="60" :since="props.row.issuedAt" />
        </td>
        <td>
          <code>
            <a
              :title="$t('actions.copyToken', { token: props.row.token })"
              @error="copyError"
              @success="copySuccess"
              href="#"
              v-clipboard="props.row.token"
            >{{shortenToken(props.row.token)}}</a>
          </code>
        </td>
        <td>
          <data-grid-actions
            :editable="false"
            :removable="isAuthenticatedAsAdmin"
            @remove="revokeTokenWhenConfirmed(props.row)"
          >
            <slot name="before">
              <li>
                <a
                  @click.prevent
                  @error="copyError"
                  @success="copySuccess"
                  href="#"
                  v-clipboard="props.row.token"
                >
                  <icon name="copy" />
                </a>
              </li>
            </slot>
          </data-grid-actions>
        </td>
      </template>
    </data-grid>
  </div>
</template>

<script>
import { findIndex } from 'lodash';
import UIkit from 'uikit';
import jwtDecode from 'jwt-decode';

import Authentication from '@/mixins/Authentication';
import CommonButton from '@/components/CommonButton';
import DataGrid from '@/components/DataGrid';
import DataGridActions from '@/components/DataGridActions';
import RevokeAllTokensButton from '@/components/RevokeAllTokensButton';
import TimeToGo from '@/components/TimeToGo';
import Toast from '@/helpers/Toast';
import RevokeTokenMutation from '@/api/mutations/revoke-token.graphql';
import IssueToken from '@/api/mutations/issue-token.graphql';
import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';

export default {
  name: 'auth-token-manager',
  mixins: [Authentication],
  components: {
    CommonButton,
    DataGrid,
    DataGridActions,
    RevokeAllTokensButton,
    TimeToGo,
    TextInput,
    SelectInput,
  },
  data() {
    return {
      authTokens: this.resource.authTokens || [],
      tempTokenTtl: '86400',
      tokencomment: '',
      ttlOptions: [
        { label: this.$t('selects.oneHour'), value: '3600' },
        { label: this.$t('selects.sixHour'), value: '21600' },
        { label: this.$t('selects.oneDay'), value: '86400' },
        { label: this.$t('selects.sevenDay'), value: '604800' },
        { label: this.$t('selects.thirtyDay'), value: '2592000' },
        { label: this.$t('selects.oneYear'), value: '31540000' },
      ],
    };
  },
  computed: {
    gridColumns() {
      return {
        expiresAt: this.$t('fields.expiredAt'),
        purpose: this.$t('fields.comment'),
        issuedAt: this.$t('fields.issuedAt'),
        token: this.$t('fields.token'),
      };
    },
    gridRows() {
      if (!this.authTokens) {
        return [];
      }

      return this.authTokens.map((authToken) => {
        const decoded = jwtDecode(authToken.token);
        return {
          ...authToken,
          expiresAt: new Date(decoded.exp * 1000).toString(),
          issuedAt: new Date(decoded.iat * 1000).toString(),
        };
      });
    },
    hasTokencomment() {
      return this.tokencomment && this.tokencomment.trim().length !== 0;
    },
  },
  methods: {
    copySuccess() {
      Toast.success(this.$t('toasts.copySuccess'));
    },
    copyError() {
      Toast.error(this.$t('toasts.copyError'));
    },
    shortenToken(token) {
      return `…${token.slice(-30)}`;
    },
    clearAuthTokens() {
      this.authTokens = [];
    },
    async revokeTokenWhenConfirmed(token) {
      try {
        const message = this.$t('confirmations.revokeToken');
        await UIkit.modal.confirm(message);
        this.revokeToken(token);
      } catch (_) {
        // Do nothing
      }
    },
    async revokeToken({ token }) {
      const options = {
        mutation: RevokeTokenMutation,
        variables: {
          token,
        },
      };

      await this.$apollo.mutate(options);
      Toast.success(this.$t('toasts.revokedToken'));

      const findFun = ({ token: current }) => current === token;
      const index = findIndex(this.authTokens, findFun);
      this.authTokens.splice(index, 1);

      this.$emit('update', this.authTokens);
    },
    async issueTempToken() {
      if (!this.hasTokencomment) {
        Toast.error(this.$t('toasts.missingcomment'));
        return;
      }
      const ttl = `${this.tempTokenTtl}`;
      if (ttl !== '0') {
        this.issueToken(ttl, this.tokencomment);
      } else {
        this.issueToken(null, this.tokencomment);
      }
    },
    async issueToken(ttl, comment) {
      const options = {
        mutation: IssueToken,
        variables: {
          tenantId: this.resource.id,
          ttl,
          comment,
        },
      };
      const response = await this.$apollo.mutate(options);
      Toast.success(this.$t('toasts.issuedToken'));
      const authToken = response.data.issueToken.authToken;
      this.authTokens.push(authToken);

      this.tokencomment = '';
      this.$emit('update', this.authTokens);
    },
  },
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          copyToken: 'Token in die Zwischenablage: {token}',
        },
        hints: {
          empty: 'Bisher wurden keine Zugangsschlüssel ausgestellt.',
        },
        buttons: {
          issueToken: 'Neuen Schlüssel ausstellen',
        },
        selects: {
          oneHour: 'eine Stunde',
          sixHour: 'sechs Stunden',
          oneDay: 'ein Tag',
          sevenDay: 'sieben Tage',
          thirtyDay: 'dreißig Tage',
          oneYear: 'ein Jahr',
        },
        confirmations: {
          revokeToken: 'Schlüssel wirklich widerrufen?',
        },
        toasts: {
          issuedToken: 'Neuer Zugangsschlüssel hinzugefügt.',
          revokedToken: 'Zugangsschlüssel entfernt.',
          copySuccess:
            'Zugangsschlüssel erfolgreich in Zwischenablage kopiert.',
          copyError:
            'Zugangsschlüssel konnte nicht in Zwischenablage kopiert werden.',
          missingcomment:
            'Der Token benötigt einen Verwendungszweck',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// AuthTokenManager.vue?47118f33